import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Briefcase, 
  FileText, 
  MessageSquare, 
  Globe, 
  CheckCircle,
  ArrowRight,
  Users,
  Building2
} from 'lucide-react';

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: FileText,
      title: 'Automatische Bewerbung',
      description: 'KI-gestützte Erstellung von Lebenslauf und Anschreiben in wenigen Minuten'
    },
    {
      icon: Briefcase,
      title: 'Job-Matching',
      description: 'Finden Sie passende Jobs basierend auf Ihren Fähigkeiten und Erfahrungen'
    },
    {
      icon: MessageSquare,
      title: 'Direkte Kommunikation',
      description: 'Interner Chat mit Arbeitgebern ohne externe Tools'
    },
    {
      icon: Globe,
      title: 'Mehrsprachig',
      description: 'Automatische Übersetzung in Deutsch, Englisch, Türkisch und Ukrainisch'
    }
  ];

  const steps = [
    { number: '1', title: 'Registrieren', description: 'Kostenloses Konto erstellen' },
    { number: '2', title: 'Profil ausfüllen', description: 'Ihre Daten eingeben' },
    { number: '3', title: 'Bewerbung generieren', description: 'KI erstellt Ihre Unterlagen' },
    { number: '4', title: 'Bewerben', description: 'Mit einem Klick bewerben' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-heading font-bold">
            <span className="text-primary">Job</span>Match
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/jobs" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
              Jobs suchen
            </Link>
            <Link to="/how-it-works" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
              So funktioniert's
            </Link>
            <Link to="/for-employers" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
              Für Arbeitgeber
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="rounded-full px-6"
                data-testid="go-to-dashboard-btn"
              >
                Zum Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="login-header-btn"
                >
                  Anmelden
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="rounded-full px-6"
                  data-testid="register-header-btn"
                >
                  Registrieren
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 leading-tight mb-6">
                Ihre Karriere.{' '}
                <span className="text-primary">Automatisiert.</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                JobMatch verbindet Bewerber und Arbeitgeber intelligent. 
                Erstellen Sie professionelle Bewerbungen mit KI und finden Sie 
                Ihren Traumjob – in Ihrer Sprache.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 gap-2"
                  onClick={() => navigate('/register')}
                  data-testid="hero-start-btn"
                >
                  Jetzt starten
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8"
                  onClick={handleGoogleLogin}
                  data-testid="hero-google-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Mit Google anmelden
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Kostenlos starten
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  DSGVO-konform
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" 
                alt="Professionelles Team" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-heading font-bold text-gray-900 mb-4">
              Alles was Sie brauchen
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Eine Plattform für Bewerber und Arbeitgeber mit intelligenten Features
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="bg-white p-6 rounded-xl border border-gray-100 hover:shadow-lg hover:border-primary/20 transition-all"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-heading font-bold text-gray-900 mb-4">
              In 4 Schritten zum Traumjob
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="font-heading font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Employers CTA */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-heading font-bold mb-6">
                Für Arbeitgeber
              </h2>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Finden Sie qualifizierte Bewerber schneller. Erstellen Sie Jobanzeigen 
                mit KI-Unterstützung und erreichen Sie internationale Talente.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 bg-white text-gray-900 hover:bg-gray-100"
                  onClick={() => navigate('/register?type=employer')}
                  data-testid="employer-cta-btn"
                >
                  Als Arbeitgeber starten
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gray-800 p-6 rounded-xl">
                <Building2 className="w-8 h-8 text-primary mb-3" />
                <h4 className="font-semibold mb-1">KI-Jobanzeigen</h4>
                <p className="text-sm text-gray-400">Automatisch optimierte Stellenbeschreibungen</p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <Users className="w-8 h-8 text-primary mb-3" />
                <h4 className="font-semibold mb-1">Smart Matching</h4>
                <p className="text-sm text-gray-400">Passende Bewerber finden</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-xl font-heading font-bold">
              <span className="text-primary">Job</span>Match
            </div>
            <div className="flex items-center gap-8 text-sm text-gray-600">
              <Link to="/privacy" className="hover:text-gray-900">Datenschutz</Link>
              <Link to="/terms" className="hover:text-gray-900">AGB</Link>
              <Link to="/imprint" className="hover:text-gray-900">Impressum</Link>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} JobMatch. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
